import { getApiErrorUserMessage, readApiError } from "_shared/_errors/api-error";
import { isFetchBaseQueryError } from "_shared/_errors/api-error-message";

/**
 * What to tell somebody whose download did not arrive.
 *
 * A refused download answers 403 with a problem body that says why — the document carries a label
 * they may not open, it is still being processed, or they have no access to it
 * (`DocumentDownloadRefusal` in `api/Documents`). That sentence is the one worth showing. "Please try
 * again" is not: trying again never changes a refusal, and it left a proposal reviewer guessing
 * whether the file was broken (#5820). The caller's own wording stays for a failure the server did not
 * explain. Only the server's answer is read: an `Error` thrown on the way — saving the file, say —
 * carries a message written for a developer, not for the person downloading.
 */
export function documentDownloadFailureMessage(error: unknown, fallback: string): string {
  if (!isFetchBaseQueryError(error)) return fallback;
  const result = readApiError(error);
  // Only a reason the server stated. A body it did not explain keeps the caller's own wording.
  return result.source === "problem-details" ? getApiErrorUserMessage(result) ?? fallback : fallback;
}

/**
 * Reads a download's error body, so a refusal's reason reaches `documentDownloadFailureMessage`.
 *
 * The download endpoint's response handler reads every body as a `Blob`, which hides a problem body
 * from the error reader. An error response is read as JSON where it is JSON, and as nothing otherwise.
 */
export async function readDownloadErrorBody(response: Response): Promise<unknown> {
  const text = await response.text().catch(() => "");
  if (!text) return undefined;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return undefined;
  }
}
